// Core
import { useTranslation } from "react-i18next";

// Components
import { Button, Crumbs, DateRangePopover, PageHead } from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Sub-views
import ListBilateralEnergyAllocations from "pages/ManageBilateralEnergyAllocations/List";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  openNotificationPopup,
  selectTimeSelector,
  updateTimeSelector,
} from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import { useExportBilateralEnergyAllocationsMutation } from "state/bilateral_energy_allocations/bilateralEnergyAllocationsApiSlice";

/* Embeddable section (used by the internal-market SideTabs and the
   standalone page): section title + export button above the allocations card,
   mirroring the internal-measurements (LoadsPage) section layout. */
export function BilateralAllocationsSection() {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const { startDate, endDate } = useSelector(selectTimeSelector);
  const { t } = useTranslation(["community"]);

  const [exportAllocations, { isLoading: isExporting }] =
    useExportBilateralEnergyAllocationsMutation();

  const handleExport = async () => {
    try {
      const blob = await exportAllocations({
        communityId: community.id,
        startDate,
        endDate,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `bilateral_energy_allocations_${startDate}_${endDate}.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      dispatch(
        openNotificationPopup({
          notification: err?.data?.detail ?? String(err),
          type: "error",
        })
      );
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h3 className="rec-section-title !mb-0">
            <Icon name="swap_horiz" />
            {t("bilateralEnergyAllocations.title")}
          </h3>
          <p className="text-[12.5px] text-ink-3 m-0 mt-1">
            {t("bilateralEnergyAllocations.subtitle")}
          </p>
        </div>
        <Button variant="ghost" size="sm" disabled={isExporting} onClick={handleExport}>
          <Icon name="download" />
          {isExporting
            ? t("bilateralEnergyAllocations.export.exporting")
            : t("bilateralEnergyAllocations.export.button")}
        </Button>
      </div>
      <ListBilateralEnergyAllocations />
    </div>
  );
}

function ManageBilateralEnergyAllocations() {
  const dispatch = useDispatch();
  const community = useSelector(selectCurrentCommunity);
  const { startDate, endDate, granularity, horizon, isExpanded } = useSelector(selectTimeSelector);
  const { t } = useTranslation(["community"]);

  const actions = (
    <div className="flex items-center gap-[10px] flex-wrap">
      <DateRangePopover
        value={{ start: startDate, end: endDate }}
        onChange={(v) =>
          dispatch(
            updateTimeSelector({
              startDate: v.start,
              endDate: v.end,
              granularity,
              horizon,
              isExpanded,
            })
          )
        }
      />
    </div>
  );

  return (
    <div>
      <Crumbs
        items={[
          { label: community?.name || "—" },
          { label: t("bilateralEnergyAllocations.title") },
        ]}
      />
      <PageHead
        title={t("bilateralEnergyAllocations.title")}
        subtitle={t("bilateralEnergyAllocations.subtitle")}
        actions={actions}
      />
      <BilateralAllocationsSection />
    </div>
  );
}

export default ManageBilateralEnergyAllocations;
